import React, { useCallback, useEffect, useRef, useState } from 'react';
import { RefreshCw, ShoppingCart } from 'lucide-react';
import { ctlApi, readCtlScope, scopeKey, useCtlScope } from './ControlsShared';
import './Controls.css';

/**
 * Consumable replenishment proposals (CTL01).
 *
 * Proposals only. Each must be approved before a purchase order is raised, so
 * the surface offers no "order" action — only the proposal and its basis.
 * An item with no closing count is shown as unmeasurable, never as zero used.
 */
export default function ReplenishmentProposals() {
  const scope = useCtlScope();
  return <Surface key={scopeKey(scope)} scope={scope} />;
}

const STATE_LABEL = {
  proposed: 'Propose order',
  unmeasurable: 'Cannot assess',
  no_order: 'No order needed',
};

function Surface({ scope }) {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState(() => new Date().toISOString().slice(0, 7));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const alive = useRef(true);
  const lifetime = useRef(new AbortController());

  const load = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      const res = await ctlApi.get('/replenishment/proposals', {
        params: { period_key: period },
        ...(lifetime.current?.signal ? { signal: lifetime.current.signal } : {}),
      });
      if (alive.current) setData(res.data);
    } catch (e) {
      if (alive.current && !e?.name?.includes('Canceled')) {
        setError(e?.response?.data?.detail || 'Could not load replenishment proposals');
      }
    } finally {
      if (alive.current) setBusy(false);
    }
  }, [period]);

  useEffect(() => {
    alive.current = true;
    lifetime.current = new AbortController();
    load();
    return () => { alive.current = false; lifetime.current?.abort(); };
  }, [load]);

  return (
    <main className="controls-workspace" aria-label="Replenishment proposals">
      <header>
        <h2>Replenishment proposals</h2>
        <button onClick={load} disabled={busy} aria-label="Refresh"><RefreshCw size={16} /></button>
      </header>

      <label>
        Period
        <input type="month" value={period} onChange={(e) => setPeriod(e.target.value)} aria-label="Period" />
      </label>

      {error && <p role="alert" className="wf-error">{error}</p>}

      {data && (
        <>
          <p>
            {data.proposal_count} proposal(s), {data.unmeasurable_count} unmeasurable. Total proposed{' '}
            {data.total_proposed}. Buffer {data.buffer_pct}%.
          </p>
          {data.notice && <p role="status" className="wf-notice">{data.notice}</p>}

          <table>
            <caption>Proposed replenishment by item</caption>
            <thead>
              <tr>
                <th scope="col">Cost centre</th><th scope="col">Item</th>
                <th scope="col">Issued</th><th scope="col">Counted</th>
                <th scope="col">Proposed</th><th scope="col">Status</th>
              </tr>
            </thead>
            <tbody>
              {data.proposals.map((p) => (
                <tr key={`${p.cost_centre_id}:${p.item_id}`} data-state={p.state}>
                  <td>{p.cost_centre_id}</td>
                  <td>{p.item_id}</td>
                  <td>{p.issued_qty}</td>
                  {/* An unmeasured item shows no count and says so, rather than a 0. */}
                  <td>{p.counted_qty === null ? <em>not counted</em> : p.counted_qty}</td>
                  <td>{p.proposed_qty === null ? <em>unknown</em> : p.proposed_qty}</td>
                  <td>
                    {STATE_LABEL[p.state] || p.state}
                    {p.state === 'unmeasurable' && <small> — {p.reason}</small>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {data.proposal_count > 0 && (
            <p role="status">
              <ShoppingCart size={16} /> Proposals must be approved before a purchase order is raised.
            </p>
          )}
        </>
      )}
    </main>
  );
}
